"use client";

import { KeyboardEvent, useEffect, useRef, useState } from "react";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import IconClose from "@/app/(components)/generics/Icons/IconClose";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./EditableText.module.css";

type Props = {
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  displayText?: string;
  multiline?: boolean;
  maxLength?: number;
  alwaysShowEdit?: boolean;
  required?: boolean;
  fitContent?: boolean;
  fullWidthOnEdit?: boolean;
  startEditingOnMount?: boolean;
  onCancel?: () => void;
  onEditClick?: () => void;
  textClassName?: string;
  inputClassName?: string;
  className?: string;
  disabled?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  alignCenter?: boolean;
};

export default function EditableText(props: Props) {
  const {
    value,
    onSave,
    placeholder,
    displayText,
    multiline = false,
    maxLength,
    alwaysShowEdit = false,
    required = false,
    fitContent = false,
    fullWidthOnEdit = false,
    startEditingOnMount = false,
    onCancel,
    onEditClick,
    textClassName,
    inputClassName,
    className,
    disabled = false,
    onEditingChange,
    alignCenter = false,
  } = props;

  const [isEditing, setIsEditing] = useState(startEditingOnMount);
  const [displayValue, setDisplayValue] = useState(value);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const keyboardSavedRef = useRef(false);

  useEffect(() => {
    if (startEditingOnMount) {
      setTimeout(() => {
        inputRef.current?.focus();
        textareaRef.current?.focus();
      }, 0);
    }
  }, [startEditingOnMount]);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const updateIsEditing = (val: boolean) => {
    setIsEditing(val);
    if (onEditingChange) {
      onEditingChange(val);
    }
  };

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (onEditClick) {
      onEditClick();
      return;
    }

    setEditValue(displayValue);
    updateIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
      textareaRef.current?.focus();
    }, 0);
  };

  const save = () => {
    const trimmed = editValue.trim();
    // Empty value with no previous content (e.g. a freshly opened "add" row left
    // blank) → cancel so the parent can drop the dangling empty entry.
    if (!trimmed && !displayValue) {
      cancel();
      return;
    }
    // If required and trying to save empty when field was previously filled → revert
    if (required && !trimmed && displayValue) {
      cancel();
      return;
    }
    if (trimmed !== displayValue) {
      onSave(trimmed);
      setDisplayValue(trimmed);
    }
    updateIsEditing(false);
  };

  const cancel = () => {
    setEditValue(value);
    updateIsEditing(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      keyboardSavedRef.current = true;
      save();
    }
    if (e.key === "Escape") {
      keyboardSavedRef.current = true;
      cancel();
    }
  };

  const handleBlur = () => {
    if (keyboardSavedRef.current) {
      keyboardSavedRef.current = false;
      return;
    }
    save();
  };

  const canSave = !required || !!editValue.trim();

  return (
    <div
      className={UStyles.classNames(
        styles.root,
        alwaysShowEdit ? styles.alwaysShowEdit : "",
        fitContent && !(isEditing && fullWidthOnEdit) ? styles.rootFitContent : "",
        className,
      )}
      onClick={(e) => isEditing && e.stopPropagation()}
    >
      {isEditing ? (
        <div
          className={UStyles.classNames(
            styles.editContainer,
            multiline ? styles.editContainerMultiline : "",
            alignCenter && !multiline ? styles.alignCenter : "",
          )}
        >
          {multiline ? (
            <textarea
              ref={textareaRef}
              value={editValue}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onChange={(e) => setEditValue(e.target.value)}
              className={UStyles.classNames(styles.textarea, inputClassName)}
              rows={3}
              maxLength={maxLength}
              placeholder={placeholder}
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onChange={(e) => setEditValue(e.target.value)}
              className={UStyles.classNames(styles.input, inputClassName)}
              maxLength={maxLength}
              placeholder={placeholder}
            />
          )}
          <div className={styles.editFooter}>
            {multiline && maxLength ? (
              <span className={styles.counter}>
                {editValue.length}/{maxLength}
              </span>
            ) : null}
            <div className={styles.actions}>
              <button
                type="button"
                className={UStyles.classNames(styles.actionBtn, styles.saveBtn)}
                disabled={!canSave}
                onMouseDown={(e) => {
                  e.preventDefault();
                  save();
                }}
              >
                <IconCheck />
              </button>
              <button
                type="button"
                className={UStyles.classNames(styles.actionBtn, styles.cancelBtn)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  cancel();
                }}
              >
                <IconClose />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={UStyles.classNames(
            styles.displayRow,
            alignCenter ? styles.alignCenter : "",
            !displayValue && !disabled ? styles.displayRowClickable : "",
          )}
          onClick={!displayValue && !disabled ? startEdit : undefined}
        >
          <span
            className={UStyles.classNames(
              styles.text,
              fitContent ? styles.textFitContent : "",
              !displayValue ? styles.placeholder : "",
              textClassName,
            )}
          >
            {displayValue ? (displayText ?? displayValue) : placeholder}
          </span>
          {!disabled && (
            <button type="button" className={styles.editBtn} onClick={startEdit}>
              <IconEdit />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
